import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";

import useJStyles from "$hooks/useJStyles";
import useShippingFile from "$hooks/useShippingFile";

import { Media, ShippingFileType, ShippingRecord } from "$types";

import { Text, Button, Popin } from "$components";
import FileUploader from "$components/FileUploader/FileUploader";
import GenericSelect from "$forms/GenericSelect";
import CField, { CFieldInputProps } from "$forms/CField";

import { getDeliveryLocationsWithoutProof } from "$utils/typeConstants/ShippingRecordProofOfDelivery";

type FormValues = {
  deliveryLocationEntityId: string;
  media: Media[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  shippingRecord: ShippingRecord;
};

function AddProofOfDeliveryPopin({
  isOpen,
  onClose,
  onSubmit,
  shippingRecord,
}: Props) {
  const { uploadShippingFile } = useShippingFile(shippingRecord?.id);

  const defaultFormValues = useMemo(
    () => ({
      deliveryLocationEntityId: null,
      media: [],
    }),
    []
  );

  const { control, reset, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });

  const deliveryLocationEntityId = watch("deliveryLocationEntityId");
  const media = watch("media");

  const { jclasses } = useJStyles();
  const classes = useStyles();

  const deliveryLocationsWithoutProof = useMemo(
    () => getDeliveryLocationsWithoutProof(shippingRecord),
    [shippingRecord]
  );

  useEffect(() => {
    reset(defaultFormValues);
  }, [reset, defaultFormValues, isOpen]);

  const handleClose = useCallback(() => {
    reset(defaultFormValues);
    onClose();
  }, [onClose, reset, defaultFormValues]);

  const onSubmitHandler = useCallback(
    async (formValues: FormValues) => {
      const mediaItem = formValues.media[0];
      if (!mediaItem) {
        return;
      }
      await uploadShippingFile({
        deliveryLocationEntityId: formValues.deliveryLocationEntityId,
        mainApiMediaId: mediaItem._id,
        name: mediaItem.name,
        type: ShippingFileType.PROOF_OF_DELIVERY,
        url: mediaItem.url,
      });
      onSubmit();
      handleClose();
    },
    [uploadShippingFile, onSubmit, handleClose]
  );

  return (
    <Popin
      openPopin={isOpen}
      toggleDrawer={handleClose}
      containerClassName={classes.popinContainer}
    >
      <div className={classes.wrapper}>
        <Text type="h2">Ajouter une preuve</Text>
        <div className={classes.lineContainer}>
          <div className={classes.inputContainer}>
            <CField
              label="Lieu de livraison concerné"
              name="deliveryLocationEntityId"
              control={control}
              required
            >
              {(props: CFieldInputProps) => (
                <GenericSelect
                  {...props}
                  options={deliveryLocationsWithoutProof}
                />
              )}
            </CField>
          </div>
        </div>
        <div className={jclasses.formSectionHead}>
          <Text type="h3">Document</Text>
        </div>
        <div className={classes.lineContainer}>
          <CField name="media" control={control}>
            {(props: CFieldInputProps) => (
              <FileUploader {...props} type="shipping" isLargeVersion isAdmin />
            )}
          </CField>
        </div>
        <div className={classes.buttonsContainer}>
          <Button color="secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit(onSubmitHandler)}
            disabled={!deliveryLocationEntityId || !media?.length}
          >
            Ajouter la preuve
          </Button>
        </div>
      </div>
    </Popin>
  );
}

export default AddProofOfDeliveryPopin;

const useStyles = createUseStyles(() => ({
  wrapper: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minWidth: 1000,
    maxWidth: 1000,
  },
  popinContainer: {
    display: "flex",
    flex: 1,
  },
  inputContainer: {
    display: "flex",
    flex: 1,
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    padding: theme.spacing(2),
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(4),
  },
}));
